/**
 * Why a lesson could not be joined.
 *
 * Only `'stopped'` is detectable today — see `LESSON_GONE_MARKERS` for why a deleted lesson is
 * deliberately NOT listed here.
 */
export type LessonGoneReason = 'stopped';

const REASON_MESSAGE: Record<LessonGoneReason, string> = {
    'stopped': 'Lesson has already stopped',
};

/**
 * Thrown by `StreamingHubService.joinLesson` when the lesson can no longer be joined.
 *
 * Signals a terminal condition: there is no point retrying the join.
 */
export class LessonStoppedError extends Error {
    override readonly name = 'LessonStoppedError';

    readonly reason: LessonGoneReason;

    constructor(reason: LessonGoneReason, cause: Error) {
        super(REASON_MESSAGE[reason], { cause });
        this.reason = reason;
        this.stack = cause.stack;
    }
}

/**
 * The server reports a stopped lesson as a `HubException`, whose message SignalR wraps as
 * `An unexpected error occurred invoking 'JoinLesson' on the server. HubException: <message>`.
 *
 * Match only the stable tail, never the whole wrapped string: the prefix already changed once
 * (service commit a4411ff3 switched `Exception` -> `HubException`) and silently broke this
 * detection for six months, leaving `joinedLessonId` set so every reconnect re-joined a dead
 * lesson. Keep this tolerant of both the wrapper wording and its absence.
 *
 * ⚠️ A **deleted** lesson is intentionally absent, and must not be added as a marker without a
 * server change first. `BaseLessonRepository.FindByIdAsync` throws `LessonNotFoundException`
 * (an `ErrorCodeException`, NOT a `HubException`) before `StreamingHub` reaches its null check,
 * so `RestoreLessonService`'s "Cannot join lesson, lesson is null" is unreachable. And because
 * `EnableDetailedErrors` is Development-only while only a `HubException`'s message is always
 * sent, production clients receive the bare
 * "An unexpected error occurred invoking 'JoinLesson' on the server." with no detail to match.
 * Verified against the running dev service 2026-10-08. Matching the dev-only
 * `LessonNotFoundException:` text would work locally and do nothing in production — exactly the
 * failure mode this file exists to prevent. The fix belongs server-side: throw a `HubException`
 * for a missing lesson, then add the marker here.
 */
const LESSON_GONE_MARKERS: ReadonlyArray<{ marker: string, reason: LessonGoneReason }> = [
    // StreamingHub.cs — lesson exists but is neither active nor waiting to start
    { marker: 'lesson has already stopped', reason: 'stopped' },
];

/**
 * @returns why the lesson is gone, or `null` when the message is some other join failure
 *          (which may be transient and is therefore worth retrying).
 */
export const lessonGoneReason = (message: string): LessonGoneReason | null =>
    LESSON_GONE_MARKERS.find(({ marker }) => message.includes(marker))?.reason ?? null;
